import classNames from 'classnames';
import debounce from 'debounce-promise';
import React from 'react';
import { Controller, FieldPath, FieldValues, PathValue, useFormContext } from 'react-hook-form';
import {
  createFilter,
  FilterOptionOption,
  GroupBase,
  OptionsOrGroups,
  SingleValue,
} from 'react-select';
import ReactAsyncSelect from 'react-select/async';
import { FormSelectInput, selectPortalProps, SelectStyles } from '~/components/forms/Select/Select';
import { FormFeedback } from '../FormFeedback';
import { FormField } from '../FormField';
import type { FormTypography } from '../FormLabel';
import { getOptionFieldError } from '../formUtils';
import { FormFieldProps } from '../interfaces';

export const MINIMUM_CHARACTERS_SEARCH = 3;

export const shouldLoadAsyncSelectOptions = (inputValue: string, minChars: boolean) => {
  return !minChars || inputValue.length >= MINIMUM_CHARACTERS_SEARCH;
};

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TOption extends { value: string | number; label: string } = {
    value: string | number;
    label: string;
  },
> extends FormFieldProps<TFieldValues, TName> {
  typography?: FormTypography;
  /** Wrap the selected label below sm; retain the desktop single-line value. */
  wrapValueOnMobile?: boolean;
  noOptionsMessage?: string;
  loadingMessage?: string;
  cacheOptions?: boolean | string | number;
  // [MCV-1536] react-select's useAsync only ever loads `defaultOptions` once,
  // on mount, when it is boolean `true` -- a value created later (e.g. via the
  // nested "Add New Facility" dialog) never appears. Passing an ARRAY instead
  // is synced reactively on identity change (verified in useAsync's source:
  // `propsDefaultOptions !== prevDefaultOptions` re-seeds state), so callers
  // that need freshly-created options to show up should hold the options list
  // in state and pass it here instead of `true`.
  defaultOptions?:
    | boolean
    | OptionsOrGroups<PathValue<TFieldValues, TName>, GroupBase<PathValue<TFieldValues, TName>>>;
  isMulti?: boolean;
  minChars?: boolean;
  // [MCV-1628] Render the dropdown menu in a portal so it escapes ancestor
  // overflow/clip boundaries (e.g. FormDialog's `overflow-y-auto` body).
  // Defaults to true: an in-flow menu is positioned `absolute` inside that
  // scroll container, so it is both clipped by it and counted in its
  // scrollable overflow -- which is what put a ~300px phantom scrollbar on
  // every dialog holding a non-portaled select. Pass `usePortal={false}`
  // only for a menu that must stay in normal document flow.
  usePortal?: boolean;
  onChange?: (option: SingleValue<TOption>) => void;
  // [MCV-1556] react-select/async defaults `filterOption` to `null`, which
  // disables its built-in client-side narrowing entirely -- confirmed in
  // react-select's useAsync source (`filterOption = _ref.filterOption ===
  // void 0 ? null : _ref.filterOption`). Every zero-arg `loadOptions` in this
  // app therefore returned its full list on every keystroke: it looked like a
  // dead search box. Default to `createFilter()` here so typing narrows
  // whatever is currently loaded; pass `null` (or a custom matcher) as an
  // escape hatch for a loader that already does exact server-side search and
  // should skip the extra client-side pass.
  filterOption?: ((option: FilterOptionOption<TOption>, inputValue: string) => boolean) | null;
  loadOptions: (
    inputValue: string
  ) => Promise<
    OptionsOrGroups<PathValue<TFieldValues, TName>, GroupBase<PathValue<TFieldValues, TName>>>
  >;
}

export const AsyncSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TOption extends { value: string | number; label: string } = {
    value: string | number;
    label: string;
  },
>(
  props: Props<TFieldValues, TName, TOption>
) => {
  const {
    name,
    description,
    cyTag,
    label,
    placeholder = 'Select...',
    disabled,
    loadingMessage = 'Loading...',
    noOptionsMessage = 'No Results',
    loadOptions,
    onChange,
    minChars = true,
    isMulti = false,
    required,
    flagged,
    tooltip,
    usePortal = true,
    errorCyTag,
    reserveFeedbackSpace,
    filterOption = createFilter<TOption>(),
    cacheOptions = true,
    defaultOptions = true,
    typography = 'default',
    wrapValueOnMobile = false,
  } = props;
  const {
    formState: { errors },
  } = useFormContext();
  const debouncedLoadOptions = React.useMemo(() => debounce(loadOptions, 300), [loadOptions]);
  const error = getOptionFieldError(errors, name);
  const errorId = `${name}-error`;
  const helperId = `${name}-helper`;
  const resolvedErrorCyTag = errorCyTag ?? (cyTag ? `${cyTag}-error` : undefined);

  return (
    <Controller
      name={name}
      render={({ field }) => {
        const hasValue = (field.value && field.value.length > 0) || field.value?.label;
        return (
          <FormField
            id={name}
            label={label}
            tooltip={tooltip}
            required={required}
            typography={typography}
            feedback={
              <FormFeedback
                error={error?.message}
                errorId={errorId}
                errorCyTag={resolvedErrorCyTag}
                description={description}
                helperId={helperId}
                reserveSpace={reserveFeedbackSpace}
              />
            }
          >
            <div data-cy={cyTag}>
              <ReactAsyncSelect
                {...field}
                inputId={name}
                value={hasValue ? field.value : null}
                unstyled
                menuPortalTarget={usePortal ? selectPortalProps.menuPortalTarget : undefined}
                menuPosition={usePortal ? selectPortalProps.menuPosition : undefined}
                styles={{
                  singleValue: (base) => ({
                    ...base,
                    overflow: 'visible',
                    ...(typography === 'comfortable' && {
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                    }),
                  }),
                  ...(typography === 'comfortable' && {
                    multiValueLabel: (base) => ({
                      ...base,
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                    }),
                  }),
                  ...(usePortal && { menuPortal: selectPortalProps.menuPortalStyle }),
                }}
                isClearable
                isDisabled={disabled}
                aria-invalid={!!error || undefined}
                aria-required={required || undefined}
                aria-errormessage={error?.message ? errorId : undefined}
                aria-describedby={error?.message ? errorId : description ? helperId : undefined}
                components={{ Input: FormSelectInput }}
                onChange={(option) => {
                  field.onChange(option);
                  onChange?.(option as SingleValue<TOption>);
                }}
                placeholder={placeholder}
                isMulti={isMulti}
                cacheOptions={cacheOptions}
                defaultOptions={defaultOptions}
                filterOption={filterOption}
                noOptionsMessage={() => noOptionsMessage}
                loadingMessage={() => loadingMessage}
                loadOptions={(inputValue: string) => {
                  return shouldLoadAsyncSelectOptions(inputValue, minChars)
                    ? debouncedLoadOptions(inputValue)
                    : Promise.resolve([]);
                }}
                classNames={{
                  ...SelectStyles(flagged, !!error, typography, wrapValueOnMobile),
                  multiValue: () => classNames('px-2 bg-white rounded-md mx-1 border-1'),
                  // [MCV-1536] `hidden` masked the per-chip remove control entirely --
                  // a multi-select value could only be cleared via Backspace or the
                  // clear-all "x", never removed individually. Restore a visible,
                  // clickable affordance (react-select's default MultiValueRemove
                  // already renders a CrossIcon; this only styles its wrapper).
                  multiValueRemove: () =>
                    classNames(
                      'px-1 rounded-r-md hover:bg-grey-900 hover:text-error-50 cursor-pointer'
                    ),
                  multiValueLabel: () =>
                    classNames(
                      'px-1 py-1 border-primary-700',
                      typography === 'comfortable'
                        ? 'text-[1rem] font-normal leading-normal text-content-primary'
                        : 'body-text'
                    ),
                }}
              />
            </div>
          </FormField>
        );
      }}
    />
  );
};
